import { ICellRendererParams } from '@ag-grid-community/core';

import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { TranslationService } from '../../../services/translation.service';
import {
	CommonColDef,
	DateColDef,
	LinkColDef,
	MultiLineColDef
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { Users, UsersDatacenter } from '../../../../users/models/user';
import { MultiLineValueTypes } from '../../cell-renderers/multi-line-value-types';
import { DatacenterDateRangeFilterComponent } from '../../filter-type/datacenter-date-range-filter.component';
import { DatacenterExactNumberFilterComponent } from '../../filter-type/datacenter-exact-number-filter.component';
import { DatacenterDropdownFilterComponent } from '../../filter-type/datacenter-dropdown-filter.component';

const linkToUserCardCellRendererParams = {
	routerLink(params: ICellRendererParams<Users, unknown>) {
		if (!params.data) {
			return null;
		}
		const { data } = params;
		return {
			commands: [
				'/user',
				'card',
				data.id,
				'settings',
			]
		};
	}
};

export class UserDatacenterColumnDefinition {
	readonly USER_REQUIRED_FIELDS = ['id'];
	readonly USER_DATA_CENTER_DEFAULT_CONFIGURATION:
	Map<string, CommonColDef<UsersDatacenter> | CommonColDef<UsersDatacenter, string>
	| CommonColDef<UsersDatacenter, Date>>;

	constructor(private readonly translationService: TranslationService) {
		this.USER_DATA_CENTER_DEFAULT_CONFIGURATION = new
		Map<string, CommonColDef<UsersDatacenter> | CommonColDef<UsersDatacenter, string>
		| CommonColDef<UsersDatacenter, Date>>([
			[
				'id',
				new LinkColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('datacenter-filter.user-id'),
					field: 'id',
					colId: 'id',
					filter: DatacenterExactNumberFilterComponent,
					suppressHeaderMenuButton: false,
					cellRendererParams: linkToUserCardCellRendererParams,
					columnType: ColumnType.TEXT_COLUMN,
					valueGetter: params => params.data ? params.data.id : ''
				})
			],
			[
				'firstName',
				new CommonColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('datacenter-filter.user-first-name'),
					field: 'firstName',
					colId: 'firstName',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'lastName',
				new CommonColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('datacenter-filter.user-last-name'),
					field: 'lastName',
					colId: 'lastName',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'role',
				new MultiLineColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('datacenter-filter.user-role'),
					colId: 'role',
					field: 'role',
					filter: DatacenterDropdownFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						return params.data.role ? this.translationService.getTR(
							`organizationrole.${ params.data.role.trim().toLowerCase() }`
						) : '';
					},
					filterParams: { userFields: ['role'] },
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'status',
				new MultiLineColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('organization.user-status-in-organization'),
					colId: 'status',
					fields: ['status', 'personStatus'],
					minWidth: 100,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { userFields: ['status', 'personStatus'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const user = params.data;
						return user.status.trim() === 'pend'
							? this.translationService.getTR('issue.pending')
							: user.personStatus.trim() === 'pend'
								? this.translationService.getTR('wait.user.validation')
								: this.translationService.getTR('active');
					},
					valueType: MultiLineValueTypes.TEXT,
					cellStyle: params =>
						this.getFontColorByStatus(params.data),
					sortable: true
				})
			],
			[
				'personStatus',
				new MultiLineColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('datacenter-filter.user-status'),
					colId: 'personStatus',
					field: 'personStatus',
					minWidth: 100,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { userFields: ['personStatus'] },
					suppressMenu: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const translationKey = this.getPersonStatusTranslationKey(params.data.personStatus);
						return this.translationService.getTR(translationKey);
					},
					valueType: MultiLineValueTypes.TEXT,
					cellStyle: params => this.getFontColorByPersonStatus(params.data?.personStatus),
					sortable: true
				})
			],
			[
				'expiryDate',
				new DateColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('user.expiryDate'),
					colId: 'expiryDate',
					field: 'expiryDate',
					minWidth: 150,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateRangeFilterComponent,
					suppressHeaderMenuButton: false,
					resizable: true,
					columnType: ColumnType.DATE_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const user = params.data;
						return user.status.trim() === 'pend' || user.personStatus.trim() === 'pend'
							? user.expiryDate
							: '';
					}
				})
			],
			[
				'creationDate',
				new DateColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('datacenter-filter.user-creation-date'),
					colId: 'creationDate',
					field: 'creationDate',
					minWidth: 150,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateRangeFilterComponent,
					suppressHeaderMenuButton: false,
					resizable: true,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'lastConnectionDate',
				new DateColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('settings.last-connection-date'),
					colId: 'lastConnectionDate',
					field: 'lastConnectionDate',
					minWidth: 150,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateRangeFilterComponent,
					suppressHeaderMenuButton: false,
					resizable: true,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'companyName',
				new CommonColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('datacenter-filter.user-organization-name'),
					field: 'companyName',
					colId: 'companyName',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'email',
				new CommonColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('datacenter-filter.user-email'),
					field: 'email',
					colId: 'email',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'servicesId',
				new MultiLineColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('user.notifications.dropdown.services'),
					colId: 'servicesId',
					field: 'servicesLabel',
					minWidth: 150,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { userFields: ['service.id', 'service.labelTranslationId'] },
					applyLikeCondition: true,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true,
				})
			],
			[
				'countryCode',
				new MultiLineColDef<UsersDatacenter>({
					headerName: this.translationService.getTR('organization.card.identity.country'),
					colId: 'countryCode',
					field: 'countryCode',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						return params.data.countryCode ? params.data.countryCodeDescription : '';
					},
					minWidth: 150,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { userFields: ['countryCode', 'countryCodeDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
		]);
	}

	private getFontColorByStatus(
		data: UsersDatacenter | null | undefined
	): { color: string } {
		if (!data) {
			return { color: 'red' };
		}
		const status = data.status.trim() === 'pend'
			? 'pend'
			: data.personStatus.trim() === 'pend'
				? 'userValidationStatus'
				: 'activ';
		switch (status) {
		case 'activ':
			return { color: 'green' };
		case 'pend':
			return { color: 'orange' };
		case 'userValidationStatus':
			return { color: 'grey' };
		default:
			return { color: 'red' };
		}
	}

	private getPersonStatusTranslationKey(personStatus?: string): string {
		switch ((personStatus ?? '').trim().toLowerCase()) {
		case 'dact':
			return 'organization.status.deactivated';
		case 'pend':
			return 'issue.pending';
		case 'del':
			return 'organization.status.deleted';
		default:
			return 'active';
		}
	}

	private getFontColorByPersonStatus(personStatus?: string): { color: string } {
		switch ((personStatus ?? '').trim().toLowerCase()) {
		case 'dact':
			return { color: 'red' };
		case 'pend':
			return { color: 'orange' };
		case 'del':
			return { color: 'red' };
		default:
			return { color: 'green' };
		}
	}
}

export const DEFAULT_VIEW_USERS_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'id',
			pinned: 'left'
		},
		{
			colId: 'firstName',
			pinned: 'left'
		},
		{
			colId: 'lastName',
			pinned: 'left'
		},
		{
			colId: 'role',
			pinned: 'left'
		},
		{
			colId: 'status',
			pinned: 'left'
		},
		{
			colId: 'companyName',
			pinned: 'left'
		},
		{
			colId: 'personStatus'
		}
	],
	sortModel: [
		{
			colId: 'id',
			sort: 'asc'
		}
	],
	filterState: [],
	type: 'users',
	isDefault: true,
	name: 'Default',
	id: -99
};
